import qs from 'qs';

export const safeRedirect = (url: string, fallback: string = '/dashboard') => {
  try {
    const target = new URL(url, window.location.origin);
    if (
      target.protocol !== 'http:' &&
      target.protocol !== 'https:' &&
      target.protocol !== 'mailto:'
    ) {
      throw new Error('Invalid protocol');
    }
    return target.toString();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
    return new URL(fallback, window.location.origin).toString();
  }
};

/**
 * Parameters for redirecting to the Add Card page
 */
export type AddCardRedirectParams = {
  /** The retailer ID (required) */
  retailerId: string;
  /** URL to redirect back to after card is added */
  returnUrl?: string;
  /** The purchaser ID to associate the card with */
  purchaserId?: string;
  /** Supplier ID for branded header display */
  supplierId?: string;
  /** Show toggle to set as default for all purchasers */
  hasDefaultForAllToggle?: boolean;
  /** Payment request ID for unauthenticated flows */
  paymentRequestId?: string;
  /** Authentication token for unauthenticated flows */
  token?: string;
};

/** Base path for the Add Card page */
const ADD_CARD_BASE_PATH = '/payments/cards/retailer';

/**
 * Builds the URL for the Add Card page.
 * Use this when you need the URL string (e.g., for <Link> components).
 *
 * @example
 * const url = buildAddCardUrl({ retailerId: '123', purchaserId: '456' });
 * // Returns: '/payments/cards/retailer/123/add?purchaserId=456'
 */
export const buildAddCardUrl = (params: AddCardRedirectParams): string => {
  const { retailerId, ...queryParams } = params;

  // Filter out undefined/null values
  const filteredParams = Object.fromEntries(
    Object.entries(queryParams).filter(([, value]) => value != null)
  );

  const query = qs.stringify(filteredParams);
  return `${ADD_CARD_BASE_PATH}/${retailerId}/add${query ? `?${query}` : ''}`;
};

/**
 * Redirects to the Add Card page using window.location.href.
 * Use this for immediate navigation to the PCI-compliant card entry page.
 *
 * @example
 * // Basic usage
 * redirectToAddCard({ retailerId: '123', purchaserId: '456' });
 *
 * // With return URL
 * redirectToAddCard({
 *   retailerId: '123',
 *   purchaserId: '456',
 *   returnUrl: window.location.href
 * });
 *
 * // For payment request flows (unauthenticated)
 * redirectToAddCard({
 *   retailerId: '123',
 *   purchaserId: '456',
 *   paymentRequestId: 'pr-789',
 *   token: 'auth-token'
 * });
 */
export const redirectToAddCard = (params: AddCardRedirectParams): void => {
  const url = buildAddCardUrl(params);
  window.location.href = safeRedirect(url);
};
